import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

// Components
import Text from "new_components/atoms/Text";
// import AILogo from "new_components/molecules/AILogo";
import ChatTemplate from "new_components/templates/ChatTemplate";
import PromptModal from "./PromptModal";
import DataSourceCard from "new_components/molecules/DataSourceCard";
import DataSourceButton from "./components/DataSourceButton";
// import Button from "new_components/atoms/Button";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Icon from "new_components/atoms/Icon";
import Tooltip from "new_components/molecules/Tooltip";
import DescriptionInput from "new_components/molecules/DescriptionInput";
import ChatDialogBox from "./components/PromptDialog";
import ChatResponse from "./components/ChatResponse";
import DataSource from "new_components/organisms/DataSource";
import ChatHistoryModal from "./components/ChatHistoryModal";
import DataSourceModal from "./components/DataSourceDrawer";
import { useToasts } from "react-toast-notifications";

// Import Nora image
import NoraImg from "assets/images/Nora.png";

// Constants
import colors from "styles/colors";
import typography, { applyTypography } from "styles/typography"

import {
  CopilotDataProvider as CopilotDataProviderV2,
  useCopilotDataContext as useCopilotDataContextV2,
} from "./Data/CopilotDataContextV2";

import {
  FeedbackProvider,
  useFeedbackContext,
} from "./Data/FeedbackDataContext";
import { FilterProvider } from "Providers/FilterProvider";

// Data
import {
  FileManagementProvider,
  useFileManagement,
} from "./UploadModalContext";
import DeleteModal from "new_components/organisms/DeleteModal";
import PresetPromptCard from "new_components/molecules/PresetPromptCard";
import Filters from "./components/Filters";

// Styles
const CopilotContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0px;
  margin-top: 10%;
  margin-bottom: 20px;
  animation: ${props => props.skipAnimation ? 'none' : 'fadeInUp 0.8s ease-out forwards'};
  opacity: ${props => props.skipAnimation ? '1' : '0'};
  transform: ${props => props.skipAnimation ? 'translateY(0)' : 'translateY(20px)'};

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SelectedData = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 0px;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const PromptOptions = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(770px, max(550px, 100%));
  margin: 0 auto;
  padding-top: 0px;
`;

const NewChatButtonContainer = styled.div`
  position: fixed;
  top: 20px;
  left: 280px;
  z-index: ${props => props.modalOpen ? 999 : 1001};
`;

const NewChatButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid ${colors.neutral[200]};
  background-color: ${colors.background.white};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;

  &:hover {
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[300]};
  }

  &:hover .custom-tooltip {
    opacity: 1;
    visibility: visible;
  }

  & * {
    pointer-events: none;
  }
`;

const CustomTooltip = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${colors.text.gray};
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1002;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid ${colors.text.gray};
  }
`;

const MessageSpacing = styled.div`
  margin-bottom: 16px;
`;

// Allowed MIME Types
const mimeToFileType = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
};

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(770px, max(550px, 100%));
  margin: 0 auto;
  z-index: 10;
  animation: ${props => (props.chatStarted || props.skipAnimation || props.initialAnimationsPlayed) ? 'none' : 'fadeInUp 0.8s ease-out 0.6s forwards'};
  opacity: ${props => (props.chatStarted || props.skipAnimation || props.initialAnimationsPlayed) ? '1' : '0'};
  transform: ${props => (props.chatStarted || props.skipAnimation || props.initialAnimationsPlayed) ? 'translateY(0)' : 'translateY(20px)'};

  @keyframes fadeInUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const NewIcon = styled.div`
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${colors.neutral[200]};
  color: ${colors.neutral[600]};
  cursor: ${(props) => (props.enabled ? `pointer !important` : "not-allowed")};

  &:hover {
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[300]};
  }
`;

const NewIconWithText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid ${colors.neutral[200]};
  color: ${colors.text.gray};
  cursor: ${(props) =>
    !props.disabled ? `pointer !important` : "not-allowed"};

  &:hover {
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[300]};
  }
`;

const PromptWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  right: 0;
`;

const ReconnectBadge = styled.div`
  position: sticky;
  top: 20px;
  left: 0;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4px 14px;
  margin-bottom: 18px;
  background: #fff0f1;
  border: 1.5px solid #ffd6d9;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(255, 0, 56, 0.08);
  color: ${colors.auxiliary.red[500]};
  font-weight: 500;
  font-size: 12px;
`;

const ReconnectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${colors.auxiliary.red[500]};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 1px 4px 0 rgba(215, 38, 61, 0.10);
  transition: background 0.2s;
  &:hover {
    background: ${colors.auxiliary.red[600]};
  }

  ${applyTypography(typography.caption.semibold)}
`;

const DataCardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 16px;
  max-width: 100%;
  
  & > div {
    flex: 0 0 calc(50% - 4px);
    
    @media (max-width: 600px) {
      flex: 0 0 100%;
    }
  }
`;

// Add new styled components for rendering cards directly
const ChatDataCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: ${colors.background.lightBlue};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  max-width: 200px;
  flex-shrink: 0;
`;

const CardText = styled.div`
  font-size: 12px;
  color: ${colors.text.gray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

// CONSIDER MOVING THIS FUNCTION TO A HELPER FILE
const getOutcomeOptions = (outcomeQ) => {
  return outcomeQ?.questions?.map((q) => {
    return {
      label: q.name,
      value: {
        id: q.id,
        title: q.name,
        level: 3,
        type: "outcome",
      },
    };
  });
};

const generateFactorConfig = (dimData, callback, removeCb, removeAllCb) => {
  const factors = dimData?.flatMap(({ factors }) => factors);

  const factorData = {
    label: "Engagement Survey",
    value: "culture",
    selectText: "Select engagement survey factors",
    settings: ["multi"],
    options: factors?.map((factor) => {
      return {
        label: factor?.title,
        value: factor,
        type: "culture",
      };
    }),
    callback: callback,
    removeCb: removeCb, // Add a remove callback
    removeAllCb: removeAllCb,
  };

  return [factorData] || [];
};

// Update the chatScreen function to render cards from raw data
const chatScreen = (
  messagesByRoom,
  roomId,
  isPending,
  scrollRef,
  dataCards,
  chatStarted,
  streamingMsg,
  pendingInitialMessage,
  promptCard,
  pendingQuestion
) => {
  if (!messagesByRoom || !roomId) return null;

  // Grab the messages array for this room ID
  const currentRoomMessages = messagesByRoom[roomId] || [];
  const lastRoomMessage =
    currentRoomMessages.length > 0
      ? currentRoomMessages[currentRoomMessages.length - 1]
      : null;

  const containerStyles = chatStarted
    ? { overflowY: "visible", paddingTop: "0px" }
    : { overflowY: "scroll", paddingTop: "80px" };

  let activeStream;
  let isStreamingActive = false;
  Object.keys(streamingMsg || {}).forEach((key) => {
    if (
      streamingMsg[key]?.status === "streaming" &&
      streamingMsg[key]?.message &&
      streamingMsg[key]?.web_chat_id === roomId
    ) {
      activeStream = streamingMsg[key];
      isStreamingActive = true;
    }
  });

  const hasPendingInitialMessage =
    isPending && currentRoomMessages.length === 0 && !!pendingInitialMessage;
  const awaitingCurrentRoomResponse = Boolean(
    isPending &&
      (hasPendingInitialMessage ||
        (lastRoomMessage &&
          (lastRoomMessage.from === "user" ||
            lastRoomMessage.type === "prompt")))
  );
  const hasActiveStream = Boolean(activeStream);
  const shouldRenderAssistantPlaceholder =
    hasActiveStream || awaitingCurrentRoomResponse;

  // Extract the card data from the dataCards component
  let cardData = [];

  // If dataCards is a React element, try to extract selectedCards from props
  if (React.isValidElement(dataCards)) {
    const { props } = dataCards;
    // Try to get selectedCards from props
    if (props && props.selectedCards && Array.isArray(props.selectedCards)) {
      cardData = props.selectedCards;
    }
  }
  // If dataCards is raw card data (array of card objects)
  else if (Array.isArray(dataCards)) {
    cardData = dataCards;
  }


  // Only show cards if we have data and this isn't the first message
  const hasCards = cardData.length > 0 && currentRoomMessages.length > 0;

  // Pull the streaming object if it matches pendingId
  return (
    <div style={containerStyles}>
      {/* Render card data if it exists and we have messages */}
      {hasCards && (
        <DataCardsContainer>
          {cardData.map((card, index) => (
            <ChatDataCard key={`${card.id || index}`}>
              <CardText>{card.title}</CardText>
            </ChatDataCard>
          ))}
        </DataCardsContainer>
      )}

      {currentRoomMessages?.length === 0 &&
        pendingInitialMessage &&
        !promptCard && (
          <MessageSpacing
            key="initial"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <ChatDialogBox message={pendingInitialMessage?.message} />
          </MessageSpacing>
        )}
      {currentRoomMessages?.length === 0 &&
        pendingInitialMessage &&
        promptCard && (
          <MessageSpacing
            key="initial"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {promptCard}
          </MessageSpacing>
        )}
      {currentRoomMessages.map((msg, index) => {
        // If it is the user prompt and it is the first message, and the promptCard is not null, show the promptCard
        if (index === 0 && promptCard) {
          return (
            <MessageSpacing
              key={msg.id || index}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {promptCard}
            </MessageSpacing>
          );
        }

        // 1) If it's a user prompt:
        if (msg.from === "user" || msg.type === "prompt") {
          return (
            <MessageSpacing
              key={msg.id || index}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <ChatDialogBox message={msg.message} />
            </MessageSpacing>
          );
        }

        // If its the last index and the streaming is active, dont show the last message because its being rendered as a streaming message
        if (index === currentRoomMessages.length - 1 && isStreamingActive) {
          return null;
        }

        // 2) If it's an assistant message (completed from messagesByRoom):
        if (msg.from === "assistant" || msg.type === "response") {
          return (
            <MessageSpacing key={msg.id || index}>
              <ChatResponse
                message={msg.message}
                isStreaming={false}
                isThinking={false}
                scrollRef={scrollRef}
                messageId={msg.id}
              />
            </MessageSpacing>
          );
        }

        return null;
      })}

      {/* Always render the component with a stable key, but control its content via props */}
      {shouldRenderAssistantPlaceholder && (
        <MessageSpacing key="ai-response-persistent">
          <ChatResponse
            message={activeStream?.message || ""}
            isStreaming={isStreamingActive}
            isThinking={!hasActiveStream && awaitingCurrentRoomResponse}
            scrollRef={scrollRef}
            messageId={activeStream?.id || "streaming"}
          />
        </MessageSpacing>
      )}

      {/* 
        This div is used for auto-scroll. 
        We'll call scrollRef.current.scrollIntoView() to jump here 
      */}
      <div ref={scrollRef} />
    </div>
  );
};

const Copilot = (props) => {
  const fileInputRef = useRef();
  const addToast = useToasts();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedPulseId = searchParams.get('pulseId');
  const {
    feedback,
    getFeedbackParams,
    pulseSurveys,
    outcomeQ,
    factorLabels,
    setSelectedFactors,
    handleSelectPulse,
    handleRemovePulse,
    factorCardData,
    setSelectedOutcomes,
    outcomeCardData,
    handleRemoveFactor,
    handleRemoveOutcome,
    selectedPulses,
    selectedOutcomes,
    selectedFactors,
    newChatClicked,
    feedbackLength,
    handleRemoveAllFactors,
    handleRemoveAllOutcomes,
    customQuestions,
    selectedCustomQuestions,
    handleSelectCustomQuestion,
    handleRemoveCustomQuestion,
    handleRemoveAllCustomQuestions,
    customQuestionsCardData,
    setSelectedCustomQuestions,
    selectedSurveys,
    surveyCardData,
    handleSelectSurvey,
    handleRemoveSurvey,
    handleRemoveAllSurveys
  } = useFeedbackContext();

  const {
    initiateChat,
    promptText,
    setPromptText,
    chatStarted: originalChatStarted,
    chatRooms,
    startNewConversation,
    selectedRoomId,
    handleRoomSelect,
    messagesEndRef,
    handleDeleteRoom: handleDelete,
    messagesByRoom,
    handleSendMessage,
    pendingInitialMessage,
    messagePending,
    selectedParams,
    streamingMsg,
    initialPendingId: pendingId,
    usedPrompt,
    feedbackLength: roomFeedbackLength,
    cleanup,
    // TODO: We need to connect the reconnect functionality to the onKeyPress event, it should only trigger if the user presses the enter key.
    reconnect,
    isWebSocketConnected,
    hasCustomDataset,
  } = useCopilotDataContextV2();

  // Use the original chatStarted value from context
  const chatStarted = originalChatStarted;

  const { uploadFile, addedFile, uploadInProgress, clearSelectedFile } =
    useFileManagement();

  const dataSourceConfig = [
    {
      label: "Outcome",
      value: "outcome",
      settings: ["multi"],
      options: getOutcomeOptions(outcomeQ), // Create a function to get the list of outcomes
      callback: setSelectedOutcomes,
      selectText: "Select outcome",
      removeCb: handleRemoveOutcome, // Add a remove callback
      removeAllCb: handleRemoveAllOutcomes,
    },
    ...generateFactorConfig(
      factorLabels,
      setSelectedFactors,
      handleRemoveFactor,
      handleRemoveAllFactors
    )
  ];

  // Add Custom Questions to the dataSourceConfig if available
  if (customQuestions?.length > 0) {
    dataSourceConfig.push({
      label: "Custom Questions",
      value: "custom",
      settings: ["multi"],
      options: customQuestions.map(q => ({
        label: q.title,
        value: {
          ...q,
          id: q?.id?.toString() || q?.id // Ensure id is a string as expected by DataSourceCard
        },
        type: "custom",
      })),
      callback: handleSelectCustomQuestion,
      selectText: "Select custom question",
      removeCb: handleRemoveAllCustomQuestions,
      removeAllCb: handleRemoveAllCustomQuestions,
    });
  }

  if (pulseSurveys?.length > 0) {
    dataSourceConfig.push({
      label: "Pulse Surveys",
      value: "pulse",
      options: pulseSurveys,
      callback: handleSelectPulse,
      removeCb: handleRemovePulse,
    })
  }

  // Add Survey Data Sources configuration
  dataSourceConfig.push({
    label: "Survey Data",
    value: "survey",
    settings: ["multi"],
    options: [], // Options will be populated by DataSourceDrawer from survey data
    callback: handleSelectSurvey,
    removeCb: handleRemoveSurvey,
    removeAllCb: handleRemoveAllSurveys,
    selectText: "Select survey data source"
  });

  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showDataSourceDrawer, setShowDataSourceDrawer] = useState(false);
  const [fileSources, setFileSources] = useState([]);
  const [sourceType, setSourceType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isInvalidFile, setIsInvalidFile] = useState(false);
  const [fileCardData, setFileCardData] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [chatSendEnabled, setChatSendEnabled] = useState(false);
  const [chatInputEnabled, setChatInputEnabled] = useState(true);
  const [promptObject, setPromptObject] = useState(null);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [hasHandledPreselectedPulse, setHasHandledPreselectedPulse] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [pendingMessageAfterReconnect, setPendingMessageAfterReconnect] = useState(null);
  const [initialAnimationsPlayed, setInitialAnimationsPlayed] = useState(false);
  const [showDataSourcesForMissingData, setShowDataSourcesForMissingData] = useState(false);
  const [skipAnimations, setSkipAnimations] = useState(false);

  // When a new chat is selected, reset the loadTimeout
  useEffect(() => {
    setLoadTimeout(false);
  }, [selectedRoomId]);

  // Simple animation system - always animate unless explicitly skipped
  useEffect(() => {
    if (skipAnimations) {
      // Skip animations immediately for in-page actions
      setInitialAnimationsPlayed(true);
    } else {
      // Play animations with a small delay for all other cases
      const timer = setTimeout(() => {
        setInitialAnimationsPlayed(true);
      }, 1200); // Animation duration

      return () => clearTimeout(timer);
    }
  }, [skipAnimations]);

  // The input will be enabled in most cases, allowing user interaction
  useEffect(() => {
    const pulseSelected = selectedPulses.length > 0;
    const outcomesSelected = selectedOutcomes.length > 0;
    const factorsSelected = selectedFactors.length > 0;
    const customQuestionsSelected = selectedCustomQuestions.length > 0;
    const feedbackProvided = feedbackLength > 0;
    // Check to see if the user has added a data source
    const dataSourcesAdded = fileCardData.length > 0;

    // Always enable input so users can interact with it and get helpful guidance
    setChatInputEnabled(true);
  }, [
    chatStarted,
    selectedPulses,
    selectedOutcomes,
    selectedFactors,
    selectedCustomQuestions,
    feedbackLength,
    fileCardData,
  ]);

  const fileUploadEnabled = true;

  // The send button will be disabled until the user has entered a message
  useEffect(() => {
    if (promptText?.length > 0 || promptObject) {
      setChatSendEnabled(true);
    } else {
      setChatSendEnabled(false);
    }
  }, [promptText, promptObject]);

  // Add effect to handle browser navigation
  useEffect(() => {
    // Listen for unload events
    const handleCleanup = () => {
      setPendingMessageAfterReconnect(null);
      cleanup();
    };

    window.addEventListener('beforeunload', handleCleanup);
    
    // Add a one-time popstate listener that triggers on navigation
    const handlePopState = () => {
      setPendingMessageAfterReconnect(null);
      cleanup();
      // Remove listener after first trigger to prevent multiple calls
      window.removeEventListener('popstate', handlePopState);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // This will run when the component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleCleanup);
      window.removeEventListener('popstate', handlePopState);
      setPendingMessageAfterReconnect(null);
      cleanup();
    };
  }, []);
  
  // Router integration - run this effect once on mount
  useEffect(() => {
    // Create a custom popstate event listener that manually checks URL changes
    const checkNavigation = () => {
      const currentPath = window.location.pathname;
      // Store the current path in sessionStorage
      const previousPath = sessionStorage.getItem('copilotPath');
      
      // If there was a previous path and it's different, we've navigated
      if (previousPath && previousPath !== currentPath) {
        setPendingMessageAfterReconnect(null);
        cleanup();
      }
      
      // Update the stored path
      sessionStorage.setItem('copilotPath', currentPath);
    };
    
    // Run once on component mount
    checkNavigation();
    
    // Set up interval to periodically check for navigation
    const intervalId = setInterval(checkNavigation, 500);
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const updateTimeout = () => {
    setLoadTimeout(false);

    setTimeout(() => {
      setLoadTimeout(true);
    }, 1000);
  }

  // Load timeout is meant to prevent the flicker of the reconnect badge when the page is loaded
  useEffect(() => {
    if (isWebSocketConnected) {
      const timeout = setTimeout(() => {
        setLoadTimeout(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }

  }, [isWebSocketConnected]);

  useEffect(() => {
    // If a file is added, add it to the fileSources array
    if (addedFile) {
      setFileSources([...fileSources, addedFile]);
      // Add the file to the fileCardData array
      setFileCardData(
        fileCardData.concat({
          type: "file",
          id: addedFile.id,
          title: addedFile.name,
          status: "New",
        })
      );
    }
  }, [addedFile]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
    if (uploadInProgress) {
      setIsInvalidFile(true);
    }
  };

  // Create function to remove file from fileSources array
  const removeFile = (fileId) => {
    const updatedFiles = fileSources.filter((file) => file.id !== fileId);
    setFileSources(updatedFiles);
    clearSelectedFile(fileId);
    setFileCardData(fileCardData.filter((card) => card.id !== fileId));
  };

  const handleDragLeave = () => {
    setIsDragging(false);
    setIsInvalidFile(false); // Reset error state when drag leaves
  };

  const handleBrowseClick = () => {
    if (chatStarted) {
      addToast.addToast("You cannot upload files during an active chat.", {
        appearance: "info",
      });
      return;
    }
    fileInputRef.current.click();
  };

  const dataCards = (
    <DataSourceCard
      dataSourceConfig={dataSourceConfig}
      chatStarted={chatStarted}
      // Add the type: factor to the selectedFactors array
      selectedCards={
        selectedParams?.length > 0
          ? selectedParams
          : [
              ...selectedPulses,
              ...factorCardData,
              ...outcomeCardData,
            ...customQuestionsCardData,
              ...fileCardData,
              ...surveyCardData,
            ]
      }
      uploadPending={uploadInProgress}
      fileClearCb={removeFile}
      feedbackLength={roomFeedbackLength || feedbackLength}
      handleRemoveAllFactors={handleRemoveAllFactors}
      handleRemoveAllOutcomes={handleRemoveAllOutcomes}
      handleRemoveAllCustomQuestions={handleRemoveAllCustomQuestions}
    />
  );

  const promptCard = usedPrompt ? (
    <PresetPromptCard prompt={usedPrompt?.title} />
  ) : null;

  // startChat
  function createChat() {} 

  // Add new styled component for the circular Nora image
  const NoraImage = styled.img`
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 24px;
    animation: ${props => props.skipAnimation ? 'none' : 'fadeInScale 0.6s ease-out 0.2s forwards'};
    opacity: ${props => props.skipAnimation ? '1' : '0'};
    transform: ${props => props.skipAnimation ? 'scale(1)' : 'scale(0.8)'};

    @keyframes fadeInScale {
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
  `;

  const AnimatedHeading = styled.div`
    animation: ${props => props.skipAnimation ? 'none' : 'fadeInSlide 0.7s ease-out 0.4s forwards'};
    opacity: ${props => props.skipAnimation ? '1' : '0'};
    transform: ${props => props.skipAnimation ? 'translateY(0)' : 'translateY(15px)'};

    @keyframes fadeInSlide {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;

  const chatHeading = (
    <CopilotContainer skipAnimation={skipAnimations || initialAnimationsPlayed}>
      <NoraImage src={NoraImg} alt="Nora" skipAnimation={skipAnimations || initialAnimationsPlayed} />
      <AnimatedHeading skipAnimation={skipAnimations || initialAnimationsPlayed}>
        <Text
          typographyStyle={typography.head.semibold}
          color={colors.text.dark}
        >
          What's on the agenda today?
        </Text>
      </AnimatedHeading>
    </CopilotContainer>
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    uploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (chatStarted) {
      addToast.addToast("You cannot upload files during an active chat.", {
        appearance: "info",
      });
      return;
    }

    if (uploadInProgress) {
      addToast.addToast("Please wait for the current upload to finish.", {
        appearance: "info",
      });
      return;
    }

    const file = event.dataTransfer.files[0];
    const fileType = file?.type;

    if (mimeToFileType[fileType]) {
      setIsInvalidFile(false); // Reset invalid state
      uploadFile(file); // Handle valid file upload
    } else {
      setIsInvalidFile(true); // Show error state
      // add an error toast
      addToast.addToast("Invalid file type. Please upload a PDF or word doc.", {
        appearance: "error",
      });
    }
  };

          const chatComponent = (
          <ChatContainer chatStarted={chatStarted}>
            {chatStarted &&
              chatScreen(
                messagesByRoom,
                selectedRoomId,
                messagePending,
                messagesEndRef,
                dataCards,
                chatStarted,
                streamingMsg,
                pendingInitialMessage,
                promptCard,
                pendingQuestion
              )}
          </ChatContainer>
        );

  // Shared validation function for both enter key and send button
  const validateAndSendMessage = () => {
    if (!chatSendEnabled) {
      return;
    }

    // Validation logic - only for new chats
    if (!chatStarted) {
      // Check if custom dataset is loaded (from NewAnalytics)
      const pulseSelected = selectedPulses.length > 0;
      const outcomesSelected = selectedOutcomes.length > 0;
      const factorsSelected = selectedFactors.length > 0;
      const customQuestionsSelected = selectedCustomQuestions.length > 0;
      const surveysSelected = selectedSurveys.length > 0;
      const feedbackProvided = feedbackLength > 0;

      // If custom dataset is loaded OR surveys with response data are selected, allow chat
      if (hasCustomDataset || (surveysSelected && selectedSurveys.some(s => s.responses?.length > 0))) {
        sendMessage();
        return;
      }

      // Only block if chat hasn't started and no valid data source with feedback is present
      if (
        !(
          (pulseSelected && feedbackProvided) ||
          (outcomesSelected && feedbackProvided) ||
          ((factorsSelected || customQuestionsSelected) && feedbackProvided) ||
          (surveysSelected && feedbackProvided) ||
          fileCardData.length > 0
        )
      ) {
        setShowDataSourcesForMissingData(true);
        setShowDataSourceDrawer(true);
        return;
      }
    }

    sendMessage();
  };

  const sendMessage = () => {
    if (!chatSendEnabled) {
      return;
    }

    if (chatStarted) {
      // If the user sends a message, and the websocket is not connected, we need to connect it before sending the message.
      if (!isWebSocketConnected) {
        // Store the message to send after reconnection
        setPendingMessageAfterReconnect(promptText);
        setPromptText(""); // Clear the input field
        reconnect();
        return;
      }

      handleSendMessage(promptText);
      // Set the pending question
      setPendingQuestion(promptText);
    } else {
      initiateChat(
        promptObject?.categoryContent?.props?.prompt?.prompt || promptText,
        feedback,
        getFeedbackParams(fileSources, promptObject)
      );
    }
    setPromptObject(null);
  };

  // Track if we're doing rapid in-page actions to skip animations
  const markInPageAction = () => {
    setSkipAnimations(true);
    // Reset after a short time to allow future animations
    setTimeout(() => {
      setSkipAnimations(false);
    }, 500);
  };

  // Calculate total selected data sources for the button
  const visibleSelectedSources = [
    ...selectedPulses,
    ...factorCardData,
    ...outcomeCardData,
    ...customQuestionsCardData,
    ...fileCardData,
    ...surveyCardData,
  ];

  // Count only real data sources (surveys, pulses, files) and only fall back to the
  // custom dataset when no manual surveys are present.
  const manualSurveyCount = surveyCardData.length;
  const surveySourceCount =
    manualSurveyCount > 0 ? manualSurveyCount : hasCustomDataset ? 1 : 0;
  const totalSelectedSources =
    selectedPulses.length + fileCardData.length + surveySourceCount;

  const inputBox = (
    <InputContainer 
      chatStarted={chatStarted} 
      skipAnimation={skipAnimations}
      initialAnimationsPlayed={initialAnimationsPlayed}
    >
        <DescriptionInput
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
        placeholder="Ask me anything..."
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        sendEnabled={chatSendEnabled}
        inputEnabled={chatInputEnabled}
        handleDrop={handleDrop}
        isDragging={isDragging}
        sourceCards={null}
        presetPrompt={promptObject}
        fileUploadEnabled={fileUploadEnabled}
        clearPrompt={() => {
          setPromptObject(null);
          setPromptText("");
        }}
        isInvalidFile={isInvalidFile}
        chatStarted={chatStarted}
        uploadRef={
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept={Object.keys(mimeToFileType).join(",")}
          />
        }
        handleEnter={() => {
          validateAndSendMessage();
        }}
        bottomActionRow={
          <Actions
            onMouseOver={(e) => {
              e.stopPropagation();
            }}
            style={{
              justifyContent: chatStarted ? "flex-end" : "space-between",
            }}
          >
            {!chatStarted && (
              <PromptOptions>
                {fileUploadEnabled && (

                <Tooltip key="uploadTooltip" tooltipText="Upload a file">
                  <NewIcon
                    style={{
                      zIndex: 999,
                      cursor: chatStarted ? "not-allowed" : "pointer",
                    }}
                    enabled={true}
                    onClick={() => handleBrowseClick()}
                  >
                    <Icon
                      style={{
                        cursor: chatStarted
                          ? "not-allowed"
                          : "pointer !important",
                        zIndex: 1,
                      }}
                      name="add"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBrowseClick();
                      }}
                      color={colors.neutral[300]}
                      size={20}
                    />
                  </NewIcon>
                </Tooltip>
                )}
                <NewIconWithText
                  disabled={!chatInputEnabled || chatStarted}
                  onClick={() => {
                    if (!chatInputEnabled || chatStarted) {
                      return;
                    }
                    markInPageAction(); // Skip animations for modal interaction
                    setShowDataSourcesForMissingData(false);
                    setShowDataSourceDrawer(true);
                  }}
                >
                  <Icon
                    name={ICON_SET.database}
                    color={colors.neutral[300]}
                    size={20}
                    style={{
                      marginRight: 5,
                      cursor: chatStarted ? "not-allowed" : "pointer !important",
                    }}
                  />
                  Survey Data{(totalSelectedSources) > 0 ? ` (${totalSelectedSources})` : ''}
                </NewIconWithText>
                <NewIconWithText
                  disabled={!chatInputEnabled || chatStarted}
                  onClick={() => {
                    if (!chatInputEnabled || chatStarted) {
                      return;
                    }
                    markInPageAction(); // Skip animations for modal interaction
                    setShowPromptModal(true);
                  }}
                >
                  <Icon
                    name={ICON_SET.layers}
                    color={colors.neutral[300]}
                    size={20}
                    style={{
                      marginRight: 5,
                      cursor: chatStarted ? "not-allowed" : "pointer !important",
                    }}
                  />
                  Prompt library
                </NewIconWithText>
              </PromptOptions>)
            }
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Tooltip
                key="historyTooltip"
                tooltipText="Chat History"
              >
                <NewIcon enabled={true}>
                  <Icon
                    name={ICON_SET.bulletList}
                    size={20}
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      markInPageAction(); // Skip animations for modal interaction
                      setShowHistoryModal(true);
                    }}
                  />
                </NewIcon>
              </Tooltip>
              <Tooltip
                key="promptTooltip"
                tooltipText={
                  !chatSendEnabled
                    ? "Message cannot be blank."
                    : "Send message"
                }
                showTip={!chatSendEnabled}
              >
                <NewIcon enabled={chatSendEnabled}>
                  <Icon
                    name={ICON_SET.send}
                    size={20}
                    style={{
                      cursor: chatSendEnabled ? "pointer" : "not-allowed",
                    }}
                    onClick={() => {
                      if (chatSendEnabled) {
                        validateAndSendMessage()
                      }
                    }}
                  />
                </NewIcon>
              </Tooltip>
            </div>
          </Actions>
        }
      />
    </InputContainer>
  );

  // Whenever messages change or we send a new message, scroll to the bottom
  React.useEffect(() => {
    if (chatStarted && messagesEndRef?.current) {
      setTimeout(() => {
        messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, [chatStarted, selectedRoomId]);

  // Add effect to handle preselected pulse
  useEffect(() => {
    if (preselectedPulseId && pulseSurveys?.length > 0 && selectedPulses?.length === 0 && !hasHandledPreselectedPulse) {
      // Find the pulse in the available pulses
      const pulse = pulseSurveys.find(p => p.id === Number(preselectedPulseId));
      if (pulse) {
        // Select the pulse using the handleSelectPulse function
        handleSelectPulse(Number(preselectedPulseId));

        // Update the url to remove the ?pulseId=336 parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        // Enable chat input since we have a pulse selected
        setChatInputEnabled(true);

        // Mark that we've handled the preselected pulse
        setHasHandledPreselectedPulse(true);
      }
    }
  }, [preselectedPulseId, pulseSurveys, handleSelectPulse, handleRemoveAllFactors, handleRemoveAllOutcomes, hasHandledPreselectedPulse]);

  // Effect to handle sending pending message after reconnection
  useEffect(() => {
    if (isWebSocketConnected && pendingMessageAfterReconnect && chatStarted) {
      // Connection is restored and we have a pending message
      handleSendMessage(pendingMessageAfterReconnect);
      setPendingQuestion(pendingMessageAfterReconnect);
      setPendingMessageAfterReconnect(null);
      // Ensure prompt text is cleared
      setPromptText("");
    }
  }, [isWebSocketConnected, pendingMessageAfterReconnect, chatStarted, handleSendMessage]);

  return (
    <>
      {chatStarted && (
        <NewChatButtonContainer modalOpen={showHistoryModal || showDataSourceDrawer || showPromptModal || !!pendingDelete}>
          <NewChatButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Clear the cards and reset state first
              setFileCardData([]);
              setFileSources([]);
              setSelectedFactors([]);
              setSelectedOutcomes([]);
              setSelectedCustomQuestions([]);
              // Clear all data sources from feedback context
              handleRemoveAllFactors();
              handleRemoveAllOutcomes();
              handleRemoveAllCustomQuestions();
              handleRemoveAllSurveys();
              // Clear pulse selections
              selectedPulses.forEach(pulse => handleRemovePulse(pulse.id));
              setChatSendEnabled(false);
              setChatInputEnabled(false);
              clearSelectedFile();
              newChatClicked();
              setLoadTimeout(false);
              setPendingMessageAfterReconnect(null);
              // Close any open modals
              setShowHistoryModal(false);
              setShowDataSourceDrawer(false);
              setShowPromptModal(false);
              setShowDataSourcesForMissingData(false);
              startNewConversation();
              // New chat will animate by default since skipAnimations = false
            }}
          >
            <Icon
              name={ICON_SET.add}
              size={18}
              color={colors.neutral[600]}
              style={{ pointerEvents: 'none' }}
            />
            <CustomTooltip className="custom-tooltip">
              Start new chat
            </CustomTooltip>
          </NewChatButton>
        </NewChatButtonContainer>
      )}
      <ChatTemplate
        chatHeading={chatHeading}
        chatComponent={chatComponent}
        chatStarted={chatStarted}
        activeRoom={selectedRoomId}
        fromPulseSurvey={!!preselectedPulseId}
        modalOpen={showHistoryModal || showDataSourceDrawer || showPromptModal || !!pendingDelete}
        startNewChat={() => {
          // Clear the cards and reset state first
          setFileCardData([]);
          setFileSources([]);
          setSelectedFactors([]);
          setSelectedOutcomes([]);
          setSelectedCustomQuestions([]);
          // Clear all data sources from feedback context
          handleRemoveAllFactors();
          handleRemoveAllOutcomes();
          handleRemoveAllCustomQuestions();
          handleRemoveAllSurveys();
          // Clear pulse selections
          selectedPulses.forEach(pulse => handleRemovePulse(pulse.id));
          setChatSendEnabled(false);
          setChatInputEnabled(false);
          clearSelectedFile();
          newChatClicked();
          setLoadTimeout(false);
          setPendingMessageAfterReconnect(null);
          // Close any open modals
          setShowHistoryModal(false);
          setShowDataSourceDrawer(false);
          setShowPromptModal(false);
          setShowDataSourcesForMissingData(false);
          startNewConversation();
          // New chat will animate by default since skipAnimations = false
        }}
        inputBox={inputBox}
        modals={
          <>
            <ChatHistoryModal
              isOpen={showHistoryModal}
              onClose={() => {
                markInPageAction(); // Skip animations when closing modal
                setShowHistoryModal(false);
              }}
              chatRooms={chatRooms}
              selectedRoomId={selectedRoomId}
              onRoomSelect={handleRoomSelect}
              onDeleteRoom={(roomId) => setPendingDelete(roomId)}
            />
            <DataSourceModal
              isOpen={showDataSourceDrawer}
              onClose={() => {
                markInPageAction(); // Skip animations when closing modal
                setShowDataSourceDrawer(false);
                setShowDataSourcesForMissingData(false);
              }}
              dataSourceConfig={dataSourceConfig}
              selectedSources={visibleSelectedSources}
              setSourceType={setSourceType}
              chatStarted={chatStarted}
              uploadPending={uploadInProgress}
              fileClearCb={removeFile}
              feedbackLength={roomFeedbackLength || feedbackLength}
              handleRemoveAllFactors={handleRemoveAllFactors}
              handleRemoveAllOutcomes={handleRemoveAllOutcomes}
              handleRemoveAllCustomQuestions={handleRemoveAllCustomQuestions}
              enableFilters={false}
              showingForMissingData={showDataSourcesForMissingData}
              onClear={() => {
                // Clear all data sources
                handleRemoveAllFactors();
                handleRemoveAllOutcomes();
                handleRemoveAllCustomQuestions();
                handleRemoveAllSurveys();
                // Clear pulse selections
                selectedPulses.forEach(pulse => handleRemovePulse(pulse.id));
                setFileCardData([]);
                setFileSources([]);
                clearSelectedFile();
              }}
              onApply={() => {
                // Close the modal - data is already applied since it's reactive
                markInPageAction(); // Skip animations when closing modal
                setShowDataSourceDrawer(false);
                setShowDataSourcesForMissingData(false);
              }}
              onApplyAndStartChat={() => {
                // Check if we now have valid data sources with feedback
                const pulseSelected = selectedPulses.length > 0;
                const outcomesSelected = selectedOutcomes.length > 0;
                const factorsSelected = selectedFactors.length > 0;
                const customQuestionsSelected = selectedCustomQuestions.length > 0;
                const surveysSelected = selectedSurveys.length > 0;
                const feedbackProvided = feedbackLength > 0;

                // If custom dataset is loaded OR surveys with response data are selected, allow chat
                if (hasCustomDataset || (surveysSelected && selectedSurveys.some(s => s.responses?.length > 0))) {
                  markInPageAction(); // Skip animations when closing modal
                  setShowDataSourceDrawer(false);
                  setShowDataSourcesForMissingData(false);
                  sendMessage();
                  return;
                }

                if (
                  (pulseSelected && feedbackProvided) ||
                  (outcomesSelected && feedbackProvided) ||
                  ((factorsSelected || customQuestionsSelected) && feedbackProvided) ||
                  (surveysSelected && feedbackProvided) ||
                  fileCardData.length > 0
                ) {
                  // Close the modal and start the chat
                  markInPageAction(); // Skip animations when closing modal
                  setShowDataSourceDrawer(false);
                  setShowDataSourcesForMissingData(false);
                  sendMessage();
                } else {
                  // Still missing valid data sources, show error
                  addToast.addToast("Your data source must have feedback.", {
                    appearance: "error",
                  });
                }
              }}
            />
            <DeleteModal
              itemName="chat history"
              isOpen={!!pendingDelete}
              onClose={() => {
                markInPageAction(); // Skip animations when closing modal
                setPendingDelete(null);
              }}
              onConfirm={() => {
                // Check if we're deleting the active room
                const isActiveRoom = pendingDelete === selectedRoomId; 

                handleDelete(pendingDelete);

                // If we deleted the active room, clear all local state
                if (isActiveRoom) {
                  setFileCardData([]);
                  setFileSources([]);
                  setSelectedFactors([]);
                  setSelectedOutcomes([]);
                  setSelectedCustomQuestions([]);
                  // Clear all data sources from feedback context
                  handleRemoveAllFactors();
                  handleRemoveAllOutcomes();
                  handleRemoveAllCustomQuestions();
                  handleRemoveAllSurveys();
                  // Clear pulse selections
                  selectedPulses.forEach(pulse => handleRemovePulse(pulse.id));
                  setChatSendEnabled(false);
                  setChatInputEnabled(false);
                  clearSelectedFile();
                  newChatClicked();
                  setLoadTimeout(false);
                }

                setPendingDelete(null);
              }}
            />
            <PromptModal
              isOpen={showPromptModal}
              onClose={() => {
                markInPageAction(); // Skip animations when closing modal
                setShowPromptModal(false);
              }}
              onConfirm={(_, selectedPromptObject) => {
                markInPageAction(); // Skip animations when closing modal
                setPromptObject(selectedPromptObject);
                setShowPromptModal(false);
              }}
            />
          </>
        }
      />
    </>
  );
};

// PropTypes
Copilot.propTypes = {
  // ...
};

const CopilotPage = () => {
  return (
    <CopilotDataProviderV2>
      <FeedbackProvider>
        <FilterProvider>
          <FileManagementProvider>
            <Copilot />
          </FileManagementProvider>
        </FilterProvider>
      </FeedbackProvider>
    </CopilotDataProviderV2>
  );
};

export default CopilotPage;
